import doctorsService from "../services/doctors";
import branchesService from "../services/branches";
import servicesService from "../services/services";
import pagesService from "../services/pages";
import galleryService from "../services/gallery";
import informationPolicyService from "../services/informationPolicy";

// Base URL for your website - Sabine Hospitals
const BASE_URL =
  process.env.REACT_APP_BASE_URL || "https://www.sabinehospitals.com";

// Static routes configuration with lastmod dates
const STATIC_ROUTES = [
  { path: "/", lastmod: new Date() },
  {
    path: "/services",
    priority: "0.9",
  },
  { path: "/blogs", priority: "0.8" },
  {
    path: "/contact-us",
    priority: "0.7",
  },
  {
    path: "/gallery",
    priority: "0.6",
  },
  {
    path: "/testimonials",
    priority: "0.6",
  },
  {
    path: "/doctors",
    priority: "0.8",
  },
  {
    path: "/consultation",
    priority: "0.7",
  },
  {
    path: "/investor-and-relations",
    priority: "0.5",
  },
  {
    path: "/careers",
    priority: "0.6",
  },
  {
    path: "/advanced-ivf",
    priority: "0.7",
  },
  {
    path: "/online-consultation",
    priority: "0.7",
  },
];

// Helper function to format date for sitemap
const formatDate = (date) => {
  if (!date) return new Date().toISOString().split("T")[0];
  return new Date(date).toISOString().split("T")[0];
};

// Helper function to escape XML characters
const escapeXml = (unsafe) => {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case "&":
        return "&amp;";
      case "'":
        return "&apos;";
      case '"':
        return "&quot;";
      default:
        return c;
    }
  });
};

// Generate URL entry for sitemap
const generateUrlEntry = (loc, lastmod = null) => {
  const escapedLoc = escapeXml(`${BASE_URL}${loc}`);
  const lastmodStr = lastmod
    ? `    <lastmod>${formatDate(lastmod)}</lastmod>\n`
    : "";

  return `  <url>
    <loc>${escapedLoc}</loc>
${lastmodStr} 
  </url>`;
};

// Fetch dynamic routes from APIs
const fetchDynamicRoutes = async () => {
  const dynamicRoutes = [];
  console.log("🚀 Starting fetchDynamicRoutes...");

  try {
    // Fetch doctors
    console.log("👨‍⚕️ Fetching doctors for sitemap...");
    const doctorsResponse = await doctorsService.getAll("?status=ACTIVE");
    console.log("👨‍⚕️ Doctors response:", doctorsResponse);

    if (doctorsResponse?.data && Array.isArray(doctorsResponse.data)) {
      console.log("👨‍⚕️ Found", doctorsResponse.data.length, "doctors");
      doctorsResponse.data.forEach((doctor) => {
        if (doctor._id) {
          dynamicRoutes.push({
            path: `/doctor-booking-detail/${doctor._id}`,
            lastmod: doctor.updatedAt || doctor.createdAt,
          });
          dynamicRoutes.push({
            path: `/book-doctor/${doctor._id}`,
            lastmod: doctor.updatedAt || doctor.createdAt,
          });
        }
      });
      console.log(
        "👨‍⚕️ Added",
        dynamicRoutes.filter((r) => r.path.includes("doctor")).length,
        "doctor routes"
      );
    } else {
      console.warn(
        "👨‍⚕️ No doctors data found or invalid format:",
        doctorsResponse
      );
    }
  } catch (error) {
    console.error("❌ Error fetching doctors for sitemap:", error);
    console.error("❌ Doctor error details:", error.message);
  }

  try {
    // Fetch branches
    console.log("🏥 Fetching branches for sitemap...");
    const branchesResponse = await branchesService.getAll("?status=ACTIVE");
    console.log("🏥 Branches response:", branchesResponse);

    if (branchesResponse?.data && Array.isArray(branchesResponse.data)) {
      console.log("🏥 Found", branchesResponse.data.length, "branches");
      branchesResponse.data.forEach((branch) => {
        if (branch.slug) {
          dynamicRoutes.push({
            path: `/branch/${branch.slug}`,
            lastmod: branch.updatedAt || branch.createdAt,
          });
        }
      });
      console.log(
        "🏥 Added",
        dynamicRoutes.filter((r) => r.path.includes("branch")).length,
        "branch routes"
      );
    } else {
      console.warn(
        "🏥 No branches data found or invalid format:",
        branchesResponse
      );
    }
  } catch (error) {
    console.error("❌ Error fetching branches for sitemap:", error);
    console.error("❌ Branch error details:", error.message);
  }

  try {
    // Fetch services
    console.log("Fetching services for sitemap...");
    const servicesResponse = await servicesService.getAll("?status=ACTIVE");
    if (servicesResponse?.data) {
      servicesResponse.data.forEach((service) => {
        if (service.slug) {
          dynamicRoutes.push({
            path: `/services/${service.slug}`,
            lastmod: service.updatedAt || service.createdAt,
          });
        }
      });
    }
  } catch (error) {
    console.error("Error fetching services for sitemap:", error);
  }

  try {
    // Fetch blog posts (pages with blog category)
    console.log("Fetching blogs for sitemap...");
    const blogsResponse = await pagesService.getAll(
      "?category=6780be1dc8a5ce81775c05c2&status=ACTIVE"
    );
    if (blogsResponse?.data) {
      blogsResponse.data.forEach((blog) => {
        if (blog.slug) {
          dynamicRoutes.push({
            path: `/blogs/${blog.slug}`,
            lastmod: blog.updatedAt || blog.createdAt,
          });
        }
      });
    }
  } catch (error) {
    console.error("Error fetching blogs for sitemap:", error);
  }

  try {
    // Fetch infertility treatment pages separately
    console.log("Fetching infertility treatment pages for sitemap...");
    const infertilityResponse = await pagesService.getAll(
      "?status=ACTIVE&categorySlug=infertility-treatment"
    );
    console.log("infertilityResponse in sitemap", infertilityResponse);
    if (infertilityResponse?.data && Array.isArray(infertilityResponse.data)) {
      infertilityResponse.data.forEach((page) => {
        // Skip deleted pages
        if (page.status === "DELETED") {
          return;
        }

        if (page.slug) {
          dynamicRoutes.push({
            path: `/infertility-treatment/${page.slug}`,
            lastmod: page.updatedAt || page.createdAt,
          });
        }
      });
      console.log(
        `✅ Found ${infertilityResponse.data.length} infertility treatment pages`
      );
    }
  } catch (error) {
    console.error(
      "Error fetching infertility treatment pages for sitemap:",
      error
    );
  }

  try {
    // Fetch custom pages separately
    console.log("Fetching custom pages for sitemap...");
    const customPagesResponse = await pagesService.getAll(
      "?status=ACTIVE&categorySlug=custom-page"
    );
    console.log("customPagesResponse in sitemap", customPagesResponse);
    if (customPagesResponse?.data && Array.isArray(customPagesResponse.data)) {
      customPagesResponse.data.forEach((page) => {
        // Skip deleted pages
        if (page.status === "DELETED") {
          return;
        }

        if (page.slug) {
          dynamicRoutes.push({
            path: `/pages/${page.slug}`,
            lastmod: page.updatedAt || page.createdAt,
          });
        }
      });
      console.log(`✅ Found ${customPagesResponse.data.length} custom pages`);
    }
  } catch (error) {
    console.error("Error fetching custom pages for sitemap:", error);
  }

  try {
    // Fetch other pages (excluding blogs, infertility treatment, and custom pages)
    console.log("Fetching regular pages for sitemap...");
    const pagesResponse = await pagesService.getAll("?status=ACTIVE");
    console.log("pagesResponse in sitemap", pagesResponse);
    if (pagesResponse?.data && Array.isArray(pagesResponse.data)) {
      pagesResponse.data.forEach((page) => {
        // Skip deleted pages, blog posts, infertility treatment pages, and custom pages
        if (
          page.status === "DELETED" ||
          page.category === "6780be1dc8a5ce81775c05c2" ||
          page.categorySlug === "infertility-treatment" ||
          page.categorySlug === "custom-page"
        ) {
          return;
        }

        if (page.slug) {
          dynamicRoutes.push({
            path: `/pages/${page.slug}`,
            lastmod: page.updatedAt || page.createdAt,
          });
        }
      });
    }
  } catch (error) {
    console.error("Error fetching pages for sitemap:", error);
  }

  try {
    // Fetch policy pages
    console.log("Fetching policy pages for sitemap...");
    const policyResponse = await informationPolicyService.getAll(
      "?status=ACTIVE"
    );
    if (policyResponse?.data && Array.isArray(policyResponse.data)) {
      policyResponse.data.forEach((policy) => {
        // Skip deleted policies
        if (policy.status === "DELETED") {
          return;
        }

        if (policy.slug) {
          dynamicRoutes.push({
            path: `/policy/${policy.slug}`,
            lastmod: policy.updatedAt || policy.createdAt,
          });
        }
      });
    }
  } catch (error) {
    console.error("Error fetching policy pages for sitemap:", error);
  }

  try {
    // Fetch gallery items
    console.log("Fetching gallery for sitemap...");
    const galleryResponse = await galleryService.getAll("?status=ACTIVE");
    if (galleryResponse?.data) {
      galleryResponse.data.forEach((gallery) => {
        if (gallery.slug) {
          dynamicRoutes.push({
            path: `/gallery/${gallery.slug}`,
            lastmod: gallery.updatedAt || gallery.createdAt,
          });
        }
      });
    }
  } catch (error) {
    console.error("Error fetching gallery for sitemap:", error);
  }

  console.log("🎯 Final dynamic routes count:", dynamicRoutes.length);
  console.log("🎯 Dynamic routes summary:", {
    doctors: dynamicRoutes.filter((r) => r.path.includes("doctor")).length,
    branches: dynamicRoutes.filter((r) => r.path.includes("branch")).length,
    services: dynamicRoutes.filter((r) => r.path.includes("services/")).length,
    blogs: dynamicRoutes.filter((r) => r.path.includes("blogs/")).length,
    pages: dynamicRoutes.filter((r) => r.path.includes("pages/")).length,
    infertility: dynamicRoutes.filter((r) =>
      r.path.includes("infertility-treatment/")
    ).length,
    policies: dynamicRoutes.filter((r) => r.path.includes("policy/")).length,
    gallery: dynamicRoutes.filter((r) => r.path.includes("gallery/")).length,
  });

  return dynamicRoutes;
};

// Generate complete sitemap XML
export const generateSitemap = async () => {
  console.log("Starting sitemap generation...");
  console.log("🔧 Environment:", process.env.NODE_ENV);
  console.log("🌐 Base URL:", BASE_URL);

  try {
    // Fetch dynamic routes
    console.log("📡 Fetching dynamic routes...");
    const dynamicRoutes = await fetchDynamicRoutes();
    console.log("📊 Dynamic routes found:", dynamicRoutes.length);
    console.log(
      "📋 Dynamic routes:",
      dynamicRoutes.map((r) => r.path)
    );

    // Combine static and dynamic routes
    const allRoutes = [...STATIC_ROUTES, ...dynamicRoutes];
    console.log(
      "📈 Total routes:",
      allRoutes.length,
      "(",
      STATIC_ROUTES.length,
      "static +",
      dynamicRoutes.length,
      "dynamic )"
    );

    // Generate XML
    const urlEntries = allRoutes
      .map((route) => generateUrlEntry(route.path, route.lastmod))
      .join("\n");

    const sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlEntries}
</urlset>`;

    console.log(
      `✅ Sitemap generated successfully with ${allRoutes.length} URLs`
    );
    console.log(
      "📄 Final sitemap preview (first 500 chars):",
      sitemap.substring(0, 500) + "..."
    );
    return sitemap;
  } catch (error) {
    console.error("Error generating sitemap:", error);

    // Fallback: generate sitemap with only static routes
    const staticUrlEntries = STATIC_ROUTES.map((route) =>
      generateUrlEntry(route.path, null)
    ).join("\n");

    const fallbackSitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${staticUrlEntries}
</urlset>`;

    console.log("Generated fallback sitemap with static routes only");
    return fallbackSitemap;
  }
};

// Generate sitemap and trigger download
export const downloadSitemap = async () => {
  try {
    const sitemapContent = await generateSitemap();

    // Create blob and download
    const blob = new Blob([sitemapContent], { type: "application/xml" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sitemap.xml";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log("Sitemap downloaded successfully");
    return true;
  } catch (error) {
    console.error("Error downloading sitemap:", error);
    return false;
  }
};

export default { generateSitemap, downloadSitemap };
